import React from "react";
import KendoUploadField from "../technical-support/components/fields/KendoUploadField";
import useContactQueryForm from "./useContactQueryForm";
import SelectField from "../technical-support/components/fields/SelectField";
function sanitizePhoneInput(value) {
  return String(value || "")
    .replace(/\D/g, "")
    .slice(0, 32);
}

function HtmlListSummary({ title, items }) {
  if (!items.length) {
    return null;
  }

  return (
    <div className="row mb-4">
      <div className="container-fluid formAlert">
        <h4>{title}</h4>
        <ul className="list-danger">
          {items.map((item, index) => (
            <li key={`${index}-${item}`} dangerouslySetInnerHTML={{ __html: item }} />
          ))}
        </ul>
      </div>
    </div>
  );
}

function ValidationSummary({ title, items }) {
  if (!items.length) {
    return null;
  }

  return (
    <div className="row mb-4">
      <div className="container-fluid formAlert">
        <h4>{title}</h4>
        <ul className="list-danger">
          {items.map((item) => (
            <li key={item}>*{item}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function getReferenceId(result) {
  return result?.ReferenceID ?? result?.referenceID ?? "";
}

function getCharactersRemaining(value, max) {
  return `Maximum characters ${max - String(value || "").length}`;
}

export default function ContactQueryFormIsland({ config }) {
  const {
    form,
    filteredItems,
    showDropdownList,
    setShowDropdownList,
    fieldErrors,
    serverSideErrors,
    submitResult,
    loading,
    recaptchaError,
    validationItems,
    updateField,
    selectOrganisation,
    addUploadedFile,
    removeFileByNameGuid,
    submitForm,
    getResponseCode,
  } = useContactQueryForm(config);

  if (submitResult) {
    if (getResponseCode(submitResult) === 200) {
      return (
        <div className="resultContent">
          <div className="w-100 text-center mb-4">
            <h3 className="mb-3 text-bolder text-darkBlue">{config.labels.thankYouTitle}</h3>
            <h2 className="text-corporateBlue text-bolder">{form.firstNameQA}</h2>
          </div>
          <div className="refNumber-cover blueBarTop">
            <div className="p-3">
              <h4 className="section-title">{config.labels.thankYouMessage}</h4>
              <div className="refNumber">
                <strong>{config.labels.referenceTitle}</strong>
                <h2>
                  <strong>{getReferenceId(submitResult)}</strong>
                </h2>
              </div>
            </div>
          </div>
          <div className="my-5 px-3 pt-3 pb-5 bg-lightest-blue">
            <div className="row">
              <div className="col-md-6">
                <h4 className="section-title">{config.labels.thankYouNextStepsTitle}</h4>
                <p className="info">
                  <strong>{config.labels.thankYouNextStepsDescription}</strong>
                </p>
                <p className="small text-corporateBlue my-3">
                  {config.labels.thankYouNextStepsDescriptionSmall}
                </p>
              </div>
            </div>
          </div>
          {config.awarenessPromoHtml ? (
            <div dangerouslySetInnerHTML={{ __html: config.awarenessPromoHtml }} />
          ) : null}
        </div>
      );
    }

    return (
      <div className="resultContent">
        <div className="w-100 text-center my-4">
          <h3 className="text-danger mb-3">{config.labels.errorFlowTitle}</h3>
          <h4 className="text-corporateBlue text-bolder">{config.labels.errorFlowSubtitle}</h4>
        </div>
        <div className="refNumber-cover blueBarTop">
          <div className="text-center">
            <h4 className="text-corporateBlue mt-5 mb-3">{config.labels.errorFlowDescription}</h4>
            <button
              type="button"
              className="btn btn-arrow-left btn-back"
              onClick={() => window.location.reload()}
            >
              <span>{config.labels.backLabel}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  const showPersonalDetailsError =
    fieldErrors.firstNameQA || fieldErrors.surnameQA || fieldErrors.emailQA;
  const showDescriptionRequiredError = fieldErrors.descQA && form.descQA.length === 0;
  const showDescriptionLengthError = fieldErrors.descQA === "maxLength";
  const showOtherOrganisation = Number(form.organisationIdQA) === -1 && config.allowNotListedOrganisation;

  return (
    <form onSubmit={submitForm}>
      <div className="w-100 text-center my-4">
        <h2 className="mb-3 text-bolder text-dark-blue">{config.labels.pageTitle}</h2>
        <h1 className="text-blue text-bolder">{config.pageSubtitle}</h1>
      </div>

      <HtmlListSummary title={config.labels.serverValidationTitle} items={serverSideErrors} />
      <ValidationSummary title={config.labels.validationTitle} items={validationItems} />

      <div className="row">
        <div className="container-fluid blueBarTop">
          <h4 className="section-title">{config.labels.personalDetailsTitle}</h4>
        </div>
        <div className="col-md-4 mb-md-4">
          <div className={`form-group ${fieldErrors.firstNameQA ? "is-invalid" : ""}`}>
            <label htmlFor="firstNameQA">*{config.labels.firstNameLabel}</label>
            <div className="validation-cover">
              <input
                id="firstNameQA"
                type="text"
                className="form-control"
                autoComplete="off"
                value={form.firstNameQA}
                placeholder={config.labels.firstNamePlaceholder}
                onChange={(event) => updateField("firstNameQA", event.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-md-4">
          <div className={`form-group ${fieldErrors.surnameQA ? "is-invalid" : ""}`}>
            <label htmlFor="surnameQA">*{config.labels.surnameLabel}</label>
            <div className="validation-cover">
              <input
                id="surnameQA"
                type="text"
                className="form-control"
                autoComplete="off"
                value={form.surnameQA}
                placeholder={config.labels.surnamePlaceholder}
                onChange={(event) => updateField("surnameQA", event.target.value)}
              />
            </div>
          </div>
        </div>
        {showPersonalDetailsError ? (
          <div className="col-md-4 mb-md-4 d-none d-md-block">
            <div className="text-danger mt-4 py-2">
              <i dangerouslySetInnerHTML={{ __html: `*${config.labels.personalDetailsErrorHtml}` }} />
            </div>
          </div>
        ) : null}
      </div>

      <div className="row mb-4">
        <div className="col-md-4">
          <div className={`form-group ${fieldErrors.emailQA ? "is-invalid" : ""}`}>
            <label htmlFor="emailQA">*{config.labels.emailLabel}</label>
            <div className="validation-cover">
              <input
                id="emailQA"
                type="text"
                className="form-control"
                autoComplete="off"
                value={form.emailQA}
                placeholder={config.labels.emailPlaceholder}
                onChange={(event) => updateField("emailQA", event.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className={`form-group ${fieldErrors.phoneQA ? "is-invalid" : ""}`}>
            <label htmlFor="phoneQA">{config.labels.telephoneLabel}</label>
            <div className="validation-cover">
              <input
                id="phoneQA"
                type="text"
                className="form-control"
                autoComplete="off"
                inputMode="numeric"
                pattern="[0-9]*"
                value={form.phoneQA}
                placeholder={config.labels.telephonePlaceholder}
                onChange={(event) => updateField("phoneQA", sanitizePhoneInput(event.target.value))}
              />
            </div>
          </div>
        </div>
        {showPersonalDetailsError ? (
          <div className="col-md-4 d-md-none mb-3">
            <div className="text-danger">
              <i dangerouslySetInnerHTML={{ __html: `*${config.labels.personalDetailsErrorHtml}` }} />
            </div>
          </div>
        ) : null}
      </div>

      <div className="row">
        <div className="container-fluid blueBarTop bg-lightest-blue">
          <h4 className="section-title">{config.labels.organisationDetailsTitle}</h4>
          <div className="row">
            <div className="col-md-4 mb-md-4">
              <div className={`form-group filter-dropdown ${fieldErrors.organisationQA ? "is-invalid" : ""}`}>
                <label htmlFor="organisationQA">
                  {config.isGasConsumerQuery ? "" : "*"}
                  {config.labels.organisationLabel}
                </label>
                <div className="validation-cover">
                  <input
                    id="organisationQA"
                    type="text"
                    className="form-control"
                    autoComplete="off"
                    value={form.organisationQA}
                    placeholder={config.labels.organisationPlaceholder}
                    onFocus={() => setShowDropdownList(true)}
                    onChange={(event) => {
                      updateField("organisationQA", event.target.value);
                      updateField("organisationIdQA", "");
                      updateField("otherOrganisationQA", "");
                      setShowDropdownList(true);
                    }}
                  />
                </div>
                {showDropdownList && filteredItems.length ? (
                  <div className="dropdown d-block">
                    <ul className="dropdown-list">
                      {filteredItems.map((item) => (
                        <li key={`${item.id}-${item.combinedTitle}`}>
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() => selectOrganisation(item)}
                          >
                            {item.id === -1 && config.allowNotListedOrganisation ? (
                              <strong>{item.combinedTitle}</strong>
                            ) : (
                              item.combinedTitle
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            </div>
            {showOtherOrganisation ? (
              <div className="col-md-4 mb-md-4">
                <div className={`form-group ${fieldErrors.otherOrganisationQA ? "is-invalid" : ""}`}>
                  <label htmlFor="otherOrganisationQA">*{config.labels.otherOrganisationLabel}</label>
                  <div className="validation-cover">
                    <input
                      id="otherOrganisationQA"
                      type="text"
                      className="form-control"
                      value={form.otherOrganisationQA}
                      placeholder={config.labels.otherOrganisationPlaceholder}
                      onFocus={() => setShowDropdownList(false)}
                      onChange={(event) => updateField("otherOrganisationQA", event.target.value)}
                    />
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          <div className="row">
            <div className="col-md-4 mb-md-4">
              <div className={`form-group ${fieldErrors.mprnQA ? "is-invalid" : ""}`}>
                <label htmlFor="mprnQA">{config.labels.mprnLabel}</label>
                <div className="formHelper">
                  <div className="validation-cover">
                    <input
                      id="mprnQA"
                      type="text"
                      className="form-control"
                      autoComplete="off"
                      value={form.mprnQA}
                      placeholder={config.labels.mprnPlaceholder}
                      onChange={(event) => updateField("mprnQA", event.target.value)}
                    />
                  </div>
                  <a href="javascript:void(0)" className="tooltip-toggle">
                    <i>{config.labels.tooltipLabel}</i>
                  </a>
                  <div className="tooltip">
                    <div className="tooltip-inner">{config.labels.tooltipDescription}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="container-fluid blueBarTop">
          <h4 className="section-title">{config.labels.querySectionTitle}</h4>
                  {config.cmsVersionShow && config.cmsDropdownList.length ? (
            <div className="row">
                <div className="col-md-8">
                    <div className="row">                          
                       <SelectField
                            field={{
                                id: "cmsVersionQA",
                                title: config.cmsVersionFieldTitle,
                                placeholder: config.labels.cmsVersionPlaceholder,
                                dropdown: config.cmsDropdownList.map((item) => ({ value: item, text: item })),
                                value: form.cmsVersionQA,
                            }}
                            required={config.cmsVersionRequired}
                            error={Boolean(fieldErrors.cmsVersionQA)}
                            onChange={(id, value) => updateField(id, value)}
                        />
                        </div>
                    </div>
                </div>
          ) : null}
          <div className="row">
            <div className="col-md-8">
              <div className={`form-group ${fieldErrors.descQA ? "is-invalid" : ""}`}>
                <label htmlFor="descQA">*{config.labels.descriptionLabel}</label>
                <div className="validation-cover">
                  <textarea
                    id="descQA"
                    className="form-control"
                    autoComplete="off"
                    rows="10"
                    value={form.descQA}
                    placeholder={config.labels.descriptionPlaceholder}
                    onChange={(event) => updateField("descQA", event.target.value)}
                  />
                </div>
                <div
                  className={`text-right mt-2 small ${
                    form.descQA.length > 4000 ? "text-danger" : "text-corporateBlue"
                  } ${fieldErrors.descQA ? "pr-7" : ""}`}
                >
                  <i>{getCharactersRemaining(form.descQA, 4000)}</i>
                </div>
              </div>
            </div>
            <div className="col-md-4 mb-3">
              {showDescriptionRequiredError ? (
                <div className="text-danger mt-4">
                  <i dangerouslySetInnerHTML={{ __html: `*${config.labels.descriptionErrorHtml}` }} />
                </div>
              ) : null}
              {showDescriptionLengthError ? (
                <div className="text-danger mt-4">
                  <i dangerouslySetInnerHTML={{ __html: `*${config.labels.descriptionLengthErrorHtml}` }} />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="container-fluid blueBarTop bg-lightest-blue pb-5">
          <h4 className="section-title">{config.labels.documentationTitle}</h4>
          <p className="text-darkBlue mb-2">
            <i>
              <strong>{config.labels.documentationSubtitle}</strong>
            </i>
          </p>
          <p className="text-darkBlue">
            <i>{config.labels.documentationFileTypes}</i>
          </p>
          <div className="row position-relative">
            <div className="toast" style={{ position: "absolute", top: 0, right: "1rem" }} data-delay="5000">
              <div className="toast-header text-danger">
                <strong className="mr-auto">{config.labels.documentationAlertTitle}</strong>
                <button type="button" className="ml-2 mb-1 close" data-bs-dismiss="toast" aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="toast-body text-danger">{config.labels.documentationAlert}</div>
            </div>
            <div className="col-md-8">
              <KendoUploadField
                fieldId="filesQA"
                title={config.labels.documentationLabel}
                multiple
                currentFiles={form.filesQA}
                allowedExtensions={config.allowedExtensions}
                fileSizeLimit={config.fileSizeLimit}
                limitOfFileNumber={config.limitOfFileNumber}
                onUploadSuccess={addUploadedFile}
                onRemoveSuccess={removeFileByNameGuid}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="container-fluid blueBarTop">
          <h4 className="section-title">{config.labels.disclaimerTitle}</h4>
          <div className="row">
            <div className="col-md-5">
              <div className={`form-group tandcCheckbox ${fieldErrors.tandcQA ? "is-invalid" : ""}`}>
                <div className="validation-cover">
                  <input
                    id="tandcQA"
                    type="checkbox"
                    checked={form.tandcQA}
                    onChange={(event) => updateField("tandcQA", event.target.checked)}
                  />
                  <label htmlFor="tandcQA">{config.labels.termsCheckboxLabel}</label>
                </div>
              </div>
            </div>
            {fieldErrors.tandcQA ? (
              <div className="col-md-4 d-flex align-items-center">
                <div className="text-danger">
                  <i dangerouslySetInnerHTML={{ __html: `*${config.labels.disclaimerErrorHtml}` }} />
                </div>
              </div>
            ) : null}
          </div>
          <div className="row">
            <div className="col-md-5">
              <div className="tandcDesc" dangerouslySetInnerHTML={{ __html: config.labels.termsDescriptionHtml }} />
            </div>
          </div>
          <div className={`form-group mt-5 ${recaptchaError ? "is-invalid" : ""}`}>
            <div className="validation-cover">
              <div id="react-island-recaptcha" />
            </div>
          </div>
        </div>
      </div>

      <div className="row my-4">
        <div className="col-6 col-md-3 offset-md-3 column mb-3 pl-0">
          <a href={config.cancelUrl} className="btn btn-wide btn-arrow-left btn-back">
            <span>{config.labels.cancelLabel}</span>
          </a>
        </div>
        <div className="col-6 col-md-3 column mb-3 pr-0">
          <button type="submit" className="btn btn-wide btn-arrow-right btn-next" disabled={loading}>
            <span>{loading ? config.labels.submittingLabel : config.labels.submitLabel}</span>
          </button>
        </div>
      </div>
    </form>
  );
}
